import { mapGetters, mapActions, mapMutations } from 'vuex'

import MarkdownEditor from '@/components/MarkdownEditor/MarkdownEditor.vue'
import SnackbarAction from '@/components/SnackbarAction/SnackbarAction.vue'
import ChatGPT from '@/components/ChatGPT/ChatGPT.vue'
import { statuses, funnel, types } from '@/utils/clusterVariables.js'
import { buildCmsUploadDecision, isCmsUploadDecisionEnabled } from '@/utils/cmsUploadDecision'

export default {
  name: 'ContentEditor',
  data () {
    return {
      isLoading: {
        articles: false,
        setStatus: null,
        setStatusPublished: false,
        setStatusReviewed: false,
        upload: null,
        reject: null,
        savingFinalDraft: false,
        rewrite: false,
        articleText: false
      },

      // Enhanced autosave configuration
      autosave: {
        timeout: null,
        delay: 1000,
        retryCount: 0,
        maxRetries: 3,
        retryDelay: 2000,
        lastSavedContent: '',
        saveInProgress: false,
        hasError: false,
        pendingSave: false
      },

      tableHeaders: [
        { text: '', value: 'select', sortable: false },
        { text: 'Name', value: 'final' },
        { text: 'Slug', value: 'slug' },
        { text: 'Status', value: 'status' },
        { text: 'Type', value: 'type' },
        { text: 'Last updated', value: 'last_updated' },
        { text: '', value: 'rewrite_type', sortable: false },
        { text: '', value: 'action' }
      ],
      tabs: [
        { text: 'Written', value: 'unpublished' },
        { text: 'Pending review', value: 'pending-review' },
        { text: 'Reviewed', value: 'reviewed' },
        { text: 'Uploaded', value: 'shared' },
        { text: 'Published', value: 'published' },
        { text: 'Dismissed', value: 'rejected' },
        { text: 'All', value: 'all' }
      ],
      activeTab: 0,
      pagination: {
        page: 1,
        limit: 200,
        totalCount: 0
      },

      // Rewrite dialog
      showRewriteDialog: false,
      rewriteArticles: [],
      rewriteStatus: null,
      rewriteTableHeaders: [
        { text: 'Title', value: 'title' },
        { text: 'Status', value: 'status' }
      ],
      statusOptions: [
        { text: 'No status - rewrite all steps', value: 'no status' },
        { text: 'Briefed - rewrite research, draft and final draft, add new images', value: 'briefed' },
        { text: 'Researched - rewrite draft and final draft, add new images', value: 'researched' },
        { text: 'Drafted - rewrite only final draft, add new images', value: 'drafted' },
        { text: 'Edited - add new images', value: 'edited' }
      ],

      selectedArticle: null,
      selectedArticleContent: null,
      articleLoadId: 0,
      statuses,
      funnel,
      types,
      selectedArticles: [],
      showSnackbar: false,
      showCopilot: false,
      uploadDialog: {
        open: false,
        title: 'Confirm upload',
        message: '',
        detail: '',
        items: [],
        confirmLabel: 'Confirm',
        confirmColor: 'primary',
        cancelLabel: 'Cancel'
      },
      uploadDialogResolver: null
    }
  },
  watch: {
    async $route () {
      this.pagination.page = 1
      await this.loadData()
    },
    async activeTab () {
      this.pagination.page = 1
      await this.loadData()
    },
    selectedArticle: {
      handler (newVal, oldVal) {
        if (oldVal && newVal?.article_id !== oldVal?.article_id) {
          // Clear any pending saves when switching articles
          this.clearAutosave()
        }

        if (newVal) {
          window.addEventListener('keydown', this.handleEscKey)
          // Initialize autosave state for new article
          this.autosave.lastSavedContent = newVal.final || ''
          this.autosave.hasError = false
          this.autosave.retryCount = 0
        } else {
          window.removeEventListener('keydown', this.handleEscKey)
          this.clearAutosave()
        }
      }
    },
    selectedArticleContent: {
      handler (newValue) {
        if (newValue !== undefined && newValue !== this.autosave.lastSavedContent) {
          this.scheduleAutosave()
        }
      },
      immediate: false
    },
    selectedArticles: {
      handler (newVal) {
        console.log('Selected articles changed:', newVal.length)
        this.showSnackbar = newVal.length > 0
      },
      deep: true
    }
  },
  computed: {
    ...mapGetters([
      'topics',
      'articles',
      'activeContentplan',
      'activeWorkspace'
    ]),
    workspaceId () {
      return parseInt(this.$route.params.workspaceId)
    },
    contentplanId () {
      return parseInt(this.$route.params.contentplanId)
    },
    contentplanName () {
      return this.activeContentplan ? this.activeContentplan.name : ''
    },
    remainingCredits () {
      return this.activeWorkspace?.remaining_article_credits
    },
    hasSelectedArticles () {
      return this.showSnackbar
    },
    snackbarActions () {
      const actions = [
        {
          text: 'Rewrite',
          event: 'rewrite-selected',
          loading: this.isLoading.rewrite,
          disabled: false
        }
      ]
      // Only show Dismiss if not in Dismissed tab
      const currentTab = this.tabs[this.activeTab]?.value
      if (currentTab !== 'rejected') {
        actions.push({
          text: 'Dismiss',
          event: 'reject-selected',
          loading: this.isLoading.reject,
          disabled: false
        })
      }
      actions.unshift({
        text: 'Upload',
        event: 'upload-selected',
        loading: this.isLoading.upload,
        disabled: this.isUploadDisabled
      })
      return actions
    },
    snackbarTitle () {
      if (!this.selectedArticles?.length) return ''
      return this.selectedArticles.length + ' article(s) selected'
    },
    isUploadDisabled () {
      return this.activeContentplan?.main_cms_integration === 'none'
    },
    uploadDisabledMessage () {
      return 'Please set up a CMS integration in Contentplan Settings before uploading articles'
    },
    contentplanSettingsUrl () {
      return {
        name: 'ContentplanSettings',
        params: {
          workspaceId: this.workspaceId,
          contentplanId: this.contentplanId
        },
        query: { tab: 'integrations' }
      }
    },
    // Enhanced computed properties for autosave
    isSaving () {
      return this.autosave.saveInProgress
    },
    hasSaveError () {
      return this.autosave.hasError
    },
    paginationTotalPages () {
      const total = Math.ceil(this.pagination.totalCount / this.pagination.limit)
      return total || 1
    },
    showPagination () {
      return this.pagination.totalCount > this.pagination.limit
    },
    paginationRangeLabel () {
      if (!this.pagination.totalCount) return '0 articles'
      const start = (this.pagination.page - 1) * this.pagination.limit + 1
      const end = Math.min(this.pagination.page * this.pagination.limit, this.pagination.totalCount)
      return `${start}-${end} of ${this.pagination.totalCount} articles`
    },
    dynamicTableHeaders () {
      const currentTab = this.tabs[this.activeTab]?.value
      return this.tableHeaders.map(header => {
        if (header.value === 'slug') {
          return {
            ...header,
            text: currentTab === 'published' ? 'URL' : 'Slug'
          }
        }
        return header
      })
    }
  },
  methods: {
    ...mapActions([
      'articleGet',
      'pageGet',
      'articlePut',
      'sendWebsocketMessage',
      'uploadArticle'
    ]),
    ...mapMutations([
      'addProgressTracker',
      'updateProgressTracker',
      'setSnackbar'
    ]),

    // Enhanced autosave methods
    scheduleAutosave () {
      if (this.autosave.saveInProgress) {
        this.autosave.pendingSave = true
        return
      }

      this.clearAutosaveTimeout()
      this.autosave.timeout = setTimeout(() => {
        this.performAutosave()
      }, this.autosave.delay)
    },

    clearAutosave () {
      this.clearAutosaveTimeout()
      this.autosave.saveInProgress = false
      this.autosave.pendingSave = false
      this.autosave.hasError = false
      this.autosave.retryCount = 0
    },

    clearAutosaveTimeout () {
      if (this.autosave.timeout) {
        clearTimeout(this.autosave.timeout)
        this.autosave.timeout = null
      }
    },

    async performAutosave () {
      if (!this.selectedArticle?.article_id || this.autosave.saveInProgress) return

      const contentToSave = this.selectedArticleContent
      if (contentToSave === this.autosave.lastSavedContent) return

      this.autosave.saveInProgress = true
      this.autosave.hasError = false
      this.isLoading.savingFinalDraft = true

      try {
        await this.articlePut({
          contentplanId: this.contentplanId,
          action: 'updateFinalDraft',
          articleIds: [this.selectedArticle.article_id],
          final: contentToSave
        })

        // Update local state on successful save
        this.autosave.lastSavedContent = contentToSave
        if (this.selectedArticle) {
          this.selectedArticle.final = contentToSave
        }
        this.autosave.retryCount = 0

        console.log('Content saved successfully')
      } catch (error) {
        console.error('Failed to save content:', error)
        this.autosave.hasError = true

        // Retry logic
        if (this.autosave.retryCount < this.autosave.maxRetries) {
          this.autosave.retryCount++
          console.log(`Retrying save (attempt ${this.autosave.retryCount}/${this.autosave.maxRetries})`)

          setTimeout(() => {
            this.performAutosave()
          }, this.autosave.retryDelay * this.autosave.retryCount)
        } else {
          this.setSnackbar({
            open: true,
            text: 'Failed to save changes after multiple attempts. Please try again later.',
            type: 'error'
          })
        }
      } finally {
        this.autosave.saveInProgress = false
        this.isLoading.savingFinalDraft = false

        // Handle pending save if there was one
        if (this.autosave.pendingSave) {
          this.autosave.pendingSave = false
          this.scheduleAutosave()
        }
      }
    },

    async loadData () {
      this.isLoading.articles = true
      document.title = 'Article editor | ContentGecko'

      try {
        const response = await this.articleGet({
          contentplanId: this.contentplanId,
          filters: this.buildContentFilters(this.tabs[this.activeTab].value),
          sort: [{ field: 'topicName', direction: 'asc' }],
          includeExtendedMetrics: false,
          pagination: this.buildPaginationParams(),
          straightReturn: true
        })
        this.pagination.totalCount = response?.meta?.totalCount || 0
        if (this.pagination.page > this.paginationTotalPages) {
          this.pagination.page = this.paginationTotalPages
          await this.loadData()
          return
        }
        const groupedArticles = this.groupArticlesByTopic(response?.items || [])
        this.$store.commit('setArticles', groupedArticles)
      } catch (error) {
        console.error('Error loading data:', error)
      }

      this.isLoading.articles = false
    },
    handlePageChange (page) {
      if (!page) return
      this.pagination.page = page
      this.clearSelectedArticles()
      this.loadData()
    },
    buildPaginationParams () {
      const limit = this.pagination.limit
      return {
        limit,
        offset: Math.max(this.pagination.page - 1, 0) * limit
      }
    },
    async editArticle (article) {
      if (!article?.article_id) return

      const loadId = ++this.articleLoadId
      this.isLoading.articleText = true

      try {
        const response = await this.articleGet({
          contentplanId: this.contentplanId,
          articleId: article.article_id,
          includeExtendedMetrics: false,
          straightReturn: true,
          skipCommit: true
        })
        if (loadId !== this.articleLoadId) return

        const full = response?.items?.[0]
        if (!full) return

        const merged = {
          ...article,
          ...full,
          last_updated: this.formatDate(full.status_updated_at),
          slug: this.extractSlugFromUrl(full.suggested_url),
          topic_slug: full.topic_name ? '/' + full.topic_name.toLowerCase().replace(/ /g, '-') : ''
        }

        this.autosave.lastSavedContent = merged.final || ''
        this.selectedArticle = merged
        this.selectedArticleContent = merged.final || ''
      } finally {
        if (loadId === this.articleLoadId) this.isLoading.articleText = false
      }
    },
    getArticleDetailViewRoute (article) {
      if (!article?.article_id) return null
      return {
        name: 'Article',
        params: {
          workspaceId: this.workspaceId,
          contentplanId: this.contentplanId,
          articleId: article.article_id
        }
      }
    },
    openArticleDetailView (article) {
      const targetRoute = this.getArticleDetailViewRoute(article)
      if (!targetRoute) return

      const { href } = this.$router.resolve(targetRoute)
      window.open(href, '_blank')
    },
    getExistingPageUrl (article) {
      if (!article?.url) return ''
      return article.url
    },
    isExistingPageRewrite (article) {
      return Boolean(article?.page_id || article?.url || article?.article_url)
    },
    getArticleRewriteChipText (article) {
      return this.isExistingPageRewrite(article) ? 'PAGE UPDATE' : 'NEW PAGE'
    },
    getArticleRewriteChipColor (article) {
      return this.isExistingPageRewrite(article) ? 'orange' : 'green'
    },
    openExistingPage (article) {
      const url = this.getExistingPageUrl(article)
      if (!url) return

      window.open(url, '_blank')
    },
    copyArticleMarkdown (article) {
      if (!article) return

      const markdown = article.final
      // Add the markdown to the clipboard
      navigator.clipboard.writeText(markdown)
    },
    copySlug (article) {
      if (!article) return

      const slug = article.slug
      // Add the slug to the clipboard
      navigator.clipboard.writeText(slug)
    },
    updateFinalDraft (newContent) {
      if (this.selectedArticle) {
        this.selectedArticle.final = newContent // Update the final draft content
        this.selectedArticleContent = newContent // Update the selected article content
      }
    },
    async refreshEditorContent () {
      if (!this.selectedArticle) return
      await this.editArticle(this.selectedArticle)
    },
    // Legacy method maintained for compatibility but improved
    async saveContent (articleId) {
      if (!articleId) return

      // Use the new autosave system for better reliability
      await this.performAutosave()
    },
    async closeEditor () {
      // Save any pending changes before closing
      if (this.selectedArticleContent !== this.autosave.lastSavedContent) {
        await this.performAutosave()
      }

      this.selectedArticle = null // Close the editor by resetting the selected article
      this.selectedArticleContent = null // Reset the selected article content
      this.showCopilot = false // Reset copilot state
      await this.loadData() // Reload
    },
    toggleCopilot () {
      this.showCopilot = !this.showCopilot
    },
    getStatusColor (status) {
      const statusObj = this.statuses.find(s => s.value === status)
      return statusObj ? statusObj.color : null
    },
    getFunnelColor (funnel) {
      const funnelObj = this.funnel.find(f => f.value === funnel)
      return funnelObj ? funnelObj.color : null
    },
    getTypeColor (type) {
      const typeObj = this.types.find(t => t.value === type)
      return typeObj ? typeObj.color : null
    },
    buildContentFilters (statusFilter) {
      const filters = [
        { field: 'final', op: 'is_null', value: false }
      ]

      if (statusFilter === 'published') filters.push({ field: 'status', op: 'eq', value: 'published' })
      if (statusFilter === 'pending-review') filters.push({ field: 'status', op: 'eq', value: 'pending-review' })
      if (statusFilter === 'reviewed') filters.push({ field: 'status', op: 'eq', value: 'reviewed' })
      if (statusFilter === 'shared') filters.push({ field: 'status', op: 'eq', value: 'shared' })
      if (statusFilter === 'unpublished') {
        filters.push({ field: 'status', op: 'not_in', value: ['published', 'shared', 'pending-review', 'reviewed'] })
      }
      if (statusFilter === 'rejected') {
        filters.push({ field: 'priority', op: 'eq', value: 'low' })
      }
      if (['published', 'pending-review', 'unpublished', 'shared', 'all'].includes(statusFilter)) {
        filters.push({ field: 'priority', op: 'not_in', value: ['low'] })
      }

      return filters
    },
    groupArticlesByTopic (articles) {
      const grouped = {}
      const noTopicSectionName = 'No topic assigned'
      const items = articles.map(article => ({
        ...article,
        last_updated: this.formatDate(article.status_updated_at),
        title: article.title,
        slug: this.extractSlugFromUrl(article.suggested_url),
        topic_slug: article.topic_name ? '/' + article.topic_name.toLowerCase().replace(/ /g, '-') : ''
      }))

      items.forEach(article => {
        const topicName = article.topic_name || noTopicSectionName
        if (!grouped[topicName]) grouped[topicName] = []
        grouped[topicName].push(article)
      })

      Object.keys(grouped).forEach(topicName => {
        grouped[topicName].sort((a, b) => {
          const aDate = new Date(a.status_updated_at || 0).getTime()
          const bDate = new Date(b.status_updated_at || 0).getTime()
          return bDate - aDate
        })
      })

      const ordered = {}
      Object.keys(grouped).sort((a, b) => {
        if (a === noTopicSectionName) return 1
        if (b === noTopicSectionName) return -1
        return a.localeCompare(b)
      }).forEach(topicName => {
        ordered[topicName] = grouped[topicName]
      })
      return ordered
    },
    formatDate (date) {
      if (!date) return ''
      const dateObj = new Date(date)
      return `${dateObj.getDate()}-${dateObj.getMonth() + 1}-${dateObj.getFullYear()}`
    },
    extractTitleFromFinal (text) {
      if (!text) return ''
      const firstLine = text.split('\n')[0]
      const firstLineCleaned = firstLine.replace(/^#+ /, '')
      return firstLineCleaned.length > 80 ? firstLineCleaned.substring(0, 80) + '...' : firstLineCleaned
    },
    extractSlugFromUrl (url) {
      if (!url) return ''
      const segments = url.split('/').filter(segment => segment)
      return '/' + segments.pop()
    },
    async setStatus (clusterId, value) {
      if (!value) return
      if (!clusterId) return

      // Set the appropriate loading state based on status
      if (value === 'published') {
        this.isLoading.setStatusPublished = true
      } else if (value === 'reviewed') {
        this.isLoading.setStatusReviewed = true
      } else {
        this.isLoading.setStatus = clusterId
      }

      try {
        await this.articlePut({
          workspaceId: this.workspaceId,
          contentplanId: this.contentplanId,
          action: 'updateStatus',
          articleIds: [clusterId],
          status: value
        })

        // close editor
        this.closeEditor()
      } finally {
        // Reset the appropriate loading state
        if (value === 'published') {
          this.isLoading.setStatusPublished = false
        } else if (value === 'reviewed') {
          this.isLoading.setStatusReviewed = false
        } else {
          this.isLoading.setStatus = false
        }
      }
    },
    async setFunnel (clusterId, value) {
      if (!value) return
      if (!clusterId) return

      this.isLoading.setFunnel = true

      try {
        await this.articlePut({
          workspaceId: this.workspaceId,
          contentplanId: this.contentplanId,
          action: 'updateFunnel',
          articleIds: [clusterId],
          funnel: value
        })
        await this.loadData()
      } finally {
        this.isLoading.setFunnel = false
      }
    },
    async setType (clusterId, value) {
      if (!value) return
      if (!clusterId) return

      this.isLoading.setType = true

      try {
        await this.articlePut({
          workspaceId: this.workspaceId,
          contentplanId: this.contentplanId,
          action: 'updateType',
          articleIds: [clusterId],
          type: value
        })
        await this.loadData()
      } finally {
        this.isLoading.setType = false
      }
    },
    async setPriority (clusterId, value) {
      if (!value) return
      if (!clusterId) return
      this.isLoading.reject = clusterId

      try {
        await this.articlePut({
          workspaceId: this.workspaceId,
          contentplanId: this.contentplanId,
          action: 'updatePriority',
          articleIds: [clusterId],
          priority: value
        })
        await this.loadData()
      } finally {
        this.isLoading.reject = null
      }
    },
    handleEscKey (event) {
      if (event.key === 'Escape') {
        this.closeEditor()
      }
    },
    getUploadDialogColor (severity) {
      if (severity === 'error') return 'error'
      if (severity === 'warning') return 'warning'
      return 'primary'
    },
    openUploadDialog ({ title, message, detail, items = [], confirmLabel, severity }) {
      if (this.uploadDialogResolver) this.uploadDialogResolver(false)
      this.uploadDialog = {
        open: true,
        title: title || 'Confirm upload',
        message: message || '',
        detail: detail || '',
        items,
        confirmLabel: confirmLabel || 'Confirm',
        confirmColor: this.getUploadDialogColor(severity),
        cancelLabel: 'Cancel'
      }

      return new Promise(resolve => {
        this.uploadDialogResolver = resolve
      })
    },
    closeUploadDialog (confirmed) {
      this.uploadDialog.open = false
      if (this.uploadDialogResolver) this.uploadDialogResolver(confirmed)
      this.uploadDialogResolver = null
    },
    cancelUploadDialog () {
      this.closeUploadDialog(false)
    },
    confirmUploadDialog () {
      this.closeUploadDialog(true)
    },
    getLocalArticleMap () {
      const map = new Map()
      if (this.selectedArticle?.article_id) map.set(this.selectedArticle.article_id, this.selectedArticle)
      this.selectedArticles.forEach(article => {
        if (article?.article_id) map.set(article.article_id, article)
      })
      Object.values(this.articles || {}).forEach(group => {
        group.forEach(article => {
          if (article?.article_id) map.set(article.article_id, article)
        })
      })
      return map
    },
    async getUploadPlans (articleIds) {
      const uniqueArticleIds = [...new Set(articleIds.filter(Boolean))]
      if (!uniqueArticleIds.length) return []

      const articleResponse = await this.articleGet({
        contentplanId: this.contentplanId,
        filters: [{ field: 'articleId', op: 'in', value: uniqueArticleIds }],
        includeExtendedMetrics: false,
        pagination: { limit: uniqueArticleIds.length, offset: 0 },
        straightReturn: true,
        skipCommit: true
      })
      const articleById = new Map((articleResponse?.items || []).map(article => [article.article_id, article]))
      const localArticles = this.getLocalArticleMap()

      uniqueArticleIds.forEach(articleId => {
        if (!articleById.has(articleId) && localArticles.has(articleId)) {
          articleById.set(articleId, localArticles.get(articleId))
        }
      })

      const pageIds = [...new Set(uniqueArticleIds.map(articleId => articleById.get(articleId)?.page_id).filter(Boolean))]
      const pageById = new Map()
      if (pageIds.length) {
        const pageResponse = await this.pageGet({
          contentplanId: this.contentplanId,
          filters: [{ field: 'pageId', op: 'in', value: pageIds }],
          pagination: { limit: pageIds.length, offset: 0 },
          skipCommit: true
        })
        const pageItems = pageResponse?.items || []
        pageItems.forEach(page => pageById.set(page.page_id, page))
      }

      const integration = this.activeContentplan?.main_cms_integration
      const decisionsUsePages = isCmsUploadDecisionEnabled(integration)

      return uniqueArticleIds.map(articleId => {
        const article = articleById.get(articleId)
        if (!article) throw new Error(`Article ${articleId} not found`)
        const page = decisionsUsePages ? (pageById.get(article.page_id) || null) : null
        const decision = buildCmsUploadDecision({ integration, article, page })
        return { articleId, article, page, decision }
      })
    },
    async confirmUploadPlans (plans) {
      if (!plans.length) return false
      if (plans.length === 1) {
        const plan = plans[0]
        return this.openUploadDialog({
          title: plan.article?.title ? `Upload "${plan.article.title}"` : 'Confirm upload',
          message: plan.decision.message,
          detail: plan.decision.detail,
          confirmLabel: plan.decision.confirmLabel,
          severity: plan.decision.severity
        })
      }

      const severity = plans.some(plan => plan.decision.severity === 'error')
        ? 'error'
        : plans.some(plan => plan.decision.severity === 'warning') ? 'warning' : 'info'

      const hasUploadAsNew = plans.some(plan => plan.decision.code === 'matched-page-without-id')
      const confirmLabel = hasUploadAsNew ? 'Upload as new page' : 'Confirm upload'
      const items = plans.map(plan => ({
        articleId: plan.articleId,
        title: plan.article?.title || `Article #${plan.articleId}`,
        message: plan.decision.message,
        detail: plan.decision.detail
      }))

      return this.openUploadDialog({
        title: `Upload ${plans.length} articles`,
        message: 'Review what is about to happen for each article.',
        items,
        confirmLabel,
        severity
      })
    },
    async handleUploadCluster (clusterId) {
      if (!clusterId) return

      if (this.isUploadDisabled) {
        this.setSnackbar({
          open: true,
          text: this.uploadDisabledMessage,
          type: 'error'
        })
        return
      }

      if (!this.activeContentplan.main_cms_integration) {
        this.setSnackbar({
          open: true,
          text: 'CMS integration is not configured. Please set up a CMS integration first.',
          type: 'error'
        })
        return
      }

      let uploadPlan = null
      try {
        const plans = await this.getUploadPlans([clusterId])
        uploadPlan = plans[0]
        const isConfirmed = await this.confirmUploadPlans(plans)
        if (!isConfirmed) return
      } catch (error) {
        console.error('Error preparing upload decision:', error)
        this.setSnackbar({
          open: true,
          text: 'Could not verify upload target. Please try again.',
          type: 'error'
        })
        return
      }

      this.isLoading.upload = clusterId

      try {
        await this.uploadArticle({
          contentplanId: this.contentplanId,
          clusterId,
          publishTo: this.activeContentplan.main_cms_integration,
          cmsIdOverride: uploadPlan?.decision?.cmsIdOverride || undefined
        })

        this.setSnackbar({
          open: true,
          text: 'Article uploaded successfully',
          type: 'success'
        })

        await this.loadData()
      } catch (error) {
        console.error('Error uploading article:', error)
        this.setSnackbar({
          open: true,
          text: error?.response?.data?.message || 'Failed to upload article. Please try again.',
          type: 'error'
        })
      } finally {
        this.isLoading.upload = null
      }
    },
    toggleSelectArticle (article) {
      const index = this.selectedArticles.findIndex(a => a.article_id === article.article_id)
      if (index === -1) this.selectedArticles.push(article)
      else this.selectedArticles.splice(index, 1)
    },
    isArticleSelected (article) {
      return this.selectedArticles.some(a => a.article_id === article.article_id)
    },
    clearSelectedArticles () {
      this.selectedArticles = []
    },
    toggleSelectAllInTopic (groupedArticles) {
      // Check if all articles in this topic are already selected
      const allSelected = this.areAllTopicArticlesSelected(groupedArticles)

      if (allSelected) {
        // If all are selected, deselect all in this topic
        this.selectedArticles = this.selectedArticles.filter(selected => {
          return !groupedArticles.some(article => article.article_id === selected.article_id)
        })
      } else {
        // If not all selected, select all articles in this topic that aren't already selected
        groupedArticles.forEach(article => {
          if (!this.isArticleSelected(article)) {
            this.selectedArticles.push(article)
          }
        })
      }
    },
    areAllTopicArticlesSelected (groupedArticles) {
      if (!groupedArticles || groupedArticles.length === 0) return false
      return groupedArticles.every(article => this.isArticleSelected(article))
    },
    async handleUploadSelected () {
      const articleIds = this.selectedArticles.map(article => article.article_id)
      if (articleIds.length === 0) return

      if (this.isUploadDisabled) {
        this.setSnackbar({
          open: true,
          text: this.uploadDisabledMessage,
          type: 'error'
        })
        return
      }

      if (!this.activeContentplan.main_cms_integration) {
        this.setSnackbar({
          open: true,
          text: 'CMS integration is not configured. Please set up a CMS integration first.',
          type: 'error'
        })
        return
      }

      let uploadPlans = []
      try {
        uploadPlans = await this.getUploadPlans(articleIds)
        const isConfirmed = await this.confirmUploadPlans(uploadPlans)
        if (!isConfirmed) return
      } catch (error) {
        console.error('Error preparing upload decision:', error)
        this.setSnackbar({
          open: true,
          text: 'Could not verify upload targets. Please try again.',
          type: 'error'
        })
        return
      }

      this.isLoading.upload = true

      try {
        await Promise.all(uploadPlans.map(plan => this.uploadArticle({
          contentplanId: this.contentplanId,
          clusterId: plan.articleId,
          publishTo: this.activeContentplan.main_cms_integration,
          cmsIdOverride: plan.decision?.cmsIdOverride || undefined
        })))

        this.clearSelectedArticles()

        await this.loadData()

        this.setSnackbar({
          open: true,
          text: articleIds.length > 1
            ? 'Articles uploaded successfully'
            : 'Article uploaded successfully',
          type: 'success'
        })
      } catch (error) {
        console.error('Error uploading articles:', error)
        this.setSnackbar({
          open: true,
          text: error?.response?.data?.message || 'Failed to upload articles. Please try again.',
          type: 'error'
        })
      } finally {
        this.isLoading.upload = false
      }
    },
    async handleRejectSelected () {
      const articleIds = this.selectedArticles.map(article => article.article_id)
      if (articleIds.length === 0) return

      this.isLoading.reject = true
      try {
        await this.articlePut({
          workspaceId: this.workspaceId,
          contentplanId: this.contentplanId,
          action: 'updatePriority',
          articleIds,
          priority: 'low'
        })
        this.clearSelectedArticles()
        await this.loadData()
      } finally {
        this.isLoading.reject = null
      }
    },
    handleSnackbarAction (event) {
      console.log('Snackbar action:', event)
      if (event === 'upload-selected') {
        this.handleUploadSelected()
      } else if (event === 'reject-selected') {
        this.handleRejectSelected()
      } else if (event === 'rewrite-selected') {
        this.openRewriteDialog()
      }
    },
    // Rewrite methods
    openRewriteDialog (article) {
      this.rewriteArticles = article ? [article] : [...this.selectedArticles]
      this.showRewriteDialog = true
      this.showSnackbar = false
    },
    closeRewriteDialog () {
      this.showRewriteDialog = false
      this.rewriteStatus = null
      this.rewriteArticles = []
    },
    async confirmRewrite () {
      if (!this.rewriteArticles.length) return // Restored this guard

      this.isLoading.rewrite = true

      try {
        // Set status for articles if status is selected
        if (this.rewriteStatus) {
          await this.articlePut({
            workspaceId: this.workspaceId,
            contentplanId: this.contentplanId,
            action: 'updateStatus',
            articleIds: this.rewriteArticles.map(article => article.article_id),
            status: this.rewriteStatus
          })
        }

        // Add progress tracker for each article
        const clusterIds = []
        for (const article of this.rewriteArticles) {
          clusterIds.push(article.article_id)

          // Determine step statuses based on rewriteStatus
          let stepsObject = {}

          // If rewriteStatus is set, use it; otherwise use the article's current status
          const statusToUse = this.rewriteStatus || article.status

          switch (statusToUse) {
            case 'briefed':
              stepsObject = {
                Briefing: 'done',
                Researching: 'inProgress',
                Drafting: 'pending',
                Editing: 'pending',
                Illustrating: 'pending'
              }
              break

            case 'researched':
              stepsObject = {
                Briefing: 'done',
                Researching: 'done',
                Drafting: 'inProgress',
                Editing: 'pending',
                Illustrating: 'pending'
              }
              break

            case 'drafted':
              stepsObject = {
                Briefing: 'done',
                Researching: 'done',
                Drafting: 'done',
                Editing: 'inProgress',
                Illustrating: 'pending'
              }
              break

            case 'edited':
              stepsObject = {
                Briefing: 'done',
                Researching: 'done',
                Drafting: 'done',
                Editing: 'done',
                Illustrating: 'inProgress'
              }
              break

            case 'no status':
            default:
              stepsObject = {
                Briefing: 'inProgress',
                Researching: 'pending',
                Drafting: 'pending',
                Editing: 'pending',
                Illustrating: 'pending'
              }
              break
          }
          this.addProgressTracker({
            contentplanId: this.contentplanId,
            clusterId: article.article_id,
            name: article.title,
            stepsObject
          })
        }
        // Run orchestrator for each article
        await this.sendWebsocketMessage({
          action: 'runOrchestrator',
          message: {
            contentplanId: this.contentplanId,
            clusterIds,
            runWriting: true,
            selectBy: 'cluster',
            publishTo: this.activeContentplan.main_cms_integration || 'autoblog',
            userType: 'managed'
          }
        })

        // Close the dialog
        this.closeRewriteDialog()
        // Clear selected articles
        this.clearSelectedArticles()
      } catch (error) {
        console.error('Error in rewrite:', error)
        // Update the tracker to show an error
        for (const article of this.rewriteArticles) {
          this.updateProgressTracker({
            contentplanId: this.contentplanId,
            clusterId: article.article_id,
            stepName: 'Briefing',
            status: 'error'
          })
        }
      } finally {
        this.isLoading.rewrite = false
      }
    }
  },
  async mounted () {
    await this.loadData()
  },
  beforeDestroy () {
    // Clean up autosave on component destruction
    this.clearAutosave()
    if (this.uploadDialogResolver) this.uploadDialogResolver(false)
  },
  components: {
    MarkdownEditor,
    SnackbarAction,
    ChatGPT
  }
}
